import React, { useState } from "react";
import { useAuth, formatError } from "@/lib/auth";
import { EnvelopeSimple, LockKey, ArrowRight } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
//
// Hero background — real Dolce Villas render (WebP, ~160 KB, 1600×1600).
// The multi-layer `background` keeps the previous brand gradient underneath
// as fallback: if the image 404s or is slow, the browser silently drops the
// image layer and the terracotta/dark gradient still renders. Same asset is
// consumed by AcceptInvitation.jsx so both public screens read as one brand.
const HERO_IMG = "/brand/dolce-villas-hero.webp";
const HERO_FALLBACK_GRADIENT = `
  radial-gradient(ellipse at 78% 18%, rgba(232,166,131,0.42), transparent 58%),
  radial-gradient(ellipse at 18% 88%, rgba(156,82,54,0.32), transparent 60%),
  linear-gradient(140deg, #1a1411 0%, #2a1c16 45%, #3a2419 100%)
`;
// Overlay sits above the photo to keep the copy readable. 55% dark-warm mix
// per design decision; if the image turns out too dark on staging, lower the
// first alpha stop toward 0.45.
const HERO_OVERLAY = `
  linear-gradient(125deg, rgba(14,13,12,0.55), rgba(26,20,17,0.35) 60%, rgba(156,82,54,0.20))
`;

// Public self-registration was removed for security reasons. The Login page
// only exposes a login form; new accounts are created by an admin through
// the admin Users page, or via the investor invitation flow (/activar).
export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email, password);
    } catch (e) {
      setErr(formatError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]" style={{ background: "#0E0D0C" }}>
      {/* LEFT — Cinematic hero: real Dolce Villas render with overlay for legibility.
          Multi-layer stack: overlay → photo → brand gradient (fallback). */}
      <div
        className="relative hidden lg:block"
        style={{
          backgroundImage: `${HERO_OVERLAY}, url('${HERO_IMG}'), ${HERO_FALLBACK_GRADIENT}`,
          backgroundSize: "cover, cover, auto",
          backgroundPosition: "center, center, center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#1a1411",
        }}
      >
        {/* Terracotta edge glow */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at top right, rgba(156,82,54,0.25), transparent 55%)" }} />

        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <div className="fade-up flex items-center gap-3">
            <div className="w-10 h-10 rounded-md flex items-center justify-center font-serif-dv text-xl"
              style={{ background: "#9C5236" }}>
              D
            </div>
            <div>
              <div className="font-serif-dv text-3xl tracking-tight leading-none">Dolce Villas</div>
              <div className="text-[10px] tracking-[0.35em] uppercase opacity-60 mt-1">Lombok · Indonesia</div>
            </div>
          </div>

          <div className="max-w-md fade-up-2">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10" style={{ background: "#9C5236" }} />
              <div className="text-[11px] tracking-[0.3em] uppercase" style={{ color: "#E8A683" }}>
                Área privada
              </div>
            </div>
            <h1 className="font-serif-dv text-5xl xl:text-6xl leading-[1.05] mb-6">
              Donde el lujo se convierte en{" "}
              <em className="not-italic" style={{ color: "#E8A683" }}>rentabilidad</em>.
            </h1>
            <p className="text-white/75 leading-relaxed max-w-sm">
              Accede a tu portafolio, documentos, avance de obra y retornos estimados.
              Todo en un solo lugar.
            </p>
          </div>

          {/* Spacer keeps the hero's flex justify-between balanced now that the
              commercial stats block (ROI / leasehold / delivery) has been
              removed. Logo stays top, copy stays mid, no orphaned bottom row. */}
          <div aria-hidden="true" />
        </div>
      </div>

      {/* RIGHT — Form on dark bg with terracotta accents */}
      <div
        className="flex items-center justify-center p-6 sm:p-12 lg:p-16 relative"
        style={{ background: "#0E0D0C" }}
      >
        {/* Subtle terracotta corner glow */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at bottom left, rgba(156,82,54,0.18), transparent 60%)" }} />

        <div className="w-full max-w-md fade-up relative z-10">
          <div className="lg:hidden mb-10 flex items-center gap-3 text-white">
            <div className="w-10 h-10 rounded-md flex items-center justify-center font-serif-dv text-xl"
              style={{ background: "#9C5236" }}>D</div>
            <div>
              <div className="font-serif-dv text-2xl leading-none">Dolce Villas</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mt-1">Lombok · Indonesia</div>
            </div>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-8" style={{ background: "#9C5236" }} />
            <div className="text-[11px] tracking-[0.3em] uppercase" style={{ color: "#E8A683" }}>
              Bienvenido de nuevo
            </div>
          </div>

          <h2 className="font-serif-dv text-4xl mb-2 text-white" data-testid="auth-title">
            Accede a tu cuenta
          </h2>
          <p className="text-white/50 mb-8">
            Gestiona tu inversión y sigue la evolución de tu villa.
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            <DarkInput icon={EnvelopeSimple} type="email" placeholder="tu@email.com"
              value={email} onChange={setEmail} required tid="auth-email-input" autoComplete="email" />

            <DarkInput icon={LockKey} type="password" placeholder="Contraseña"
              value={password} onChange={setPassword} required tid="auth-password-input"
              autoComplete="current-password" minLength={6} />

            {err && (
              <div className="text-sm px-3 py-2 rounded-md"
                style={{ background: "rgba(156,82,54,0.15)", color: "#E8A683", border: "1px solid rgba(156,82,54,0.35)" }}
                data-testid="auth-error">
                {err}
              </div>
            )}

            <button
              type="submit" disabled={busy}
              data-testid="auth-submit-btn"
              className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-md font-medium text-white transition-all hover:-translate-y-0.5 disabled:opacity-60"
              style={{
                background: "#9C5236",
                boxShadow: "0 10px 30px -10px rgba(156,82,54,0.55)",
              }}
            >
              {busy ? "Cargando…" : "Entrar"}
              <ArrowRight size={18} />
            </button>
          </form>

          <div className="mt-8 text-sm text-white/50 text-center" data-testid="auth-invitation-only">
            El acceso es solo por invitación. Si necesitas una cuenta, contacta con tu asesor de Dolce Villas.
          </div>

          <div className="mt-12 text-[10px] text-white/30 text-center tracking-wider">
            © {new Date().getFullYear()} DOLCE VILLAS LOMBOK · ÁREA PRIVADA
          </div>
        </div>
      </div>
    </div>
  );
}

function DarkInput({ icon: Icon, type, placeholder, value, onChange, required, tid, autoComplete, minLength }) {
  return (
    <div className="relative">
      <Icon size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" weight="regular" />
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={tid}
        autoComplete={autoComplete}
        minLength={minLength}
        className="w-full pl-11 pr-4 py-3 rounded-md text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
        style={{
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.12)",
        }}
      />
    </div>
  );
}
